// Vigencias de un mismo NÚMERO de póliza.
//
// Una renovación puede conservar el número de la póliza (la aseguradora renueva
// con el mismo número). Cuando eso pasa hay VARIOS registros en `polizas` con el
// mismo `numero`, uno por vigencia. Cualquier mapa `numero -> póliza` se queda
// con UNA sola vigencia, elegida por el orden accidental en que PostgREST
// devolvió las filas — y el que acierte o no es pura casualidad.
//
// El daño no es cosmético:
//   · un cobro se aplica a la vigencia vieja → cuota fantasma allá y la cuota
//     real de la vigencia nueva sigue pendiente;
//   · un pago YA aplicado sale como "falta" → se registra dos veces;
//   · una comisión nace colgada de la vigencia equivocada;
//   · un pago de la aseguradora no encuentra sus comisiones y se salta en silencio.
//
// Estas utilidades son la forma correcta de trabajar con el número de póliza:
// agrupar TODAS las vigencias y elegir la que toca por fila.

// Clave de comparación de un número de póliza: sin espacios, guiones, puntos ni
// barras, en mayúsculas. Cada aseguradora escribe el mismo número a su manera
// (Optima reporta "02 03 2345" y la carátula dice "02-03-2345").
export function claveNumero(numero) {
  return String(numero ?? '').toUpperCase().replace(/[^0-9A-Z]/g, '');
}

// Agrupa las pólizas por número. Devuelve { numero: [vigencias] }, cada lista
// ordenada de la MÁS VIEJA a la MÁS NUEVA (por fecha_inicio, desempate por id).
// `clave` opcional para agrupar por otra forma del número (ej. `claveNumero`).
export function agruparVigencias(polizas, clave) {
  const mapa = {};
  for (const p of (polizas || [])) {
    const k = clave ? clave(p.numero) : String(p.numero ?? '').trim();
    if (!k) continue;
    (mapa[k] = mapa[k] || []).push(p);
  }
  Object.values(mapa).forEach(arr => arr.sort((a, b) =>
    (a.fecha_inicio || '').localeCompare(b.fecha_inicio || '') || a.id - b.id));
  return mapa;
}

// ¿Esta vigencia puede recibir el dinero?
//   · `pendMap`  poliza_id -> cuotas pendientes/parciales
//   · `totalMap` poliza_id -> cuotas existentes (0 = todavía sin plan de cuotas)
// Sin `pendMap` no hay información de saldo y toda vigencia se considera apta
// (los importadores de comisiones no consultan cuotas).
// `totalMap` es lo que necesita el importador de pagos, que si la vigencia aún
// no tiene plan se lo GENERA: una vigencia sin cuotas sí puede recibir el pago.
export function puedeRecibir(v, pendMap, totalMap) {
  if (!pendMap) return true;
  if ((pendMap[v.id] || 0) > 0) return true;
  return !!totalMap && (totalMap[v.id] || 0) === 0;
}

const vigenciasVivas = vigs => {
  const vivas = (vigs || []).filter(v => v.estado !== 'cancelada' && v.estado !== 'anulada');
  return vivas.length ? vivas : (vigs || []);
};

const cubrenFecha = (arr, fecha) => arr.filter(v =>
  fecha && v.fecha_inicio && v.fecha_vencimiento &&
  v.fecha_inicio <= fecha && fecha <= v.fecha_vencimiento);

// Elige a qué VIGENCIA de un mismo número se le aplica un movimiento con fecha
// `fecha`. Prioridad:
//   1. la vigencia que CUBRE la fecha y puede recibir el dinero
//   2. la más RECIENTE que pueda recibirlo — a propósito la más reciente: el saco
//      camina hacia ATRÁS por `poliza_anterior_id`, así que parado en la vigencia
//      nueva se ven también las cuotas viejas, pero parado en la vieja las nuevas
//      quedan invisibles (era justo lo que rompía el caso de renovación)
//   3. si ninguna puede recibir, la que cubre la fecha
//   4. si tampoco, la más reciente
// Las canceladas/anuladas solo se usan si no hay ninguna otra opción.
// Con varias vigencias cubriendo la misma fecha (traslapes raros) se prefiere la
// más reciente, por la misma razón del punto 2.
export function elegirVigencia(vigs, fecha, pendMap, totalMap) {
  const lista = vigenciasVivas(vigs);
  if (lista.length <= 1) return lista[0] || null;

  const aptas       = lista.filter(v => puedeRecibir(v, pendMap, totalMap));
  const cubreApta   = cubrenFecha(aptas, fecha);
  if (cubreApta.length) return cubreApta[cubreApta.length - 1];
  if (aptas.length)     return aptas[aptas.length - 1];

  const cubre = cubrenFecha(lista, fecha);
  return cubre.length ? cubre[cubre.length - 1] : lista[lista.length - 1];
}

// Deja los contadores de una vigencia igualitos a sus cuotas reales. Lo usa el
// importador de pagos, que va cambiando las cuotas mientras aplica cada fila.
export function sincronizarCuotas(polizaId, cuotas, pendMap, totalMap) {
  totalMap[polizaId] = (cuotas || []).length;
  pendMap[polizaId]  = (cuotas || []).filter(c => ['pendiente', 'parcial'].includes(c.estado)).length;
}
